import { useMemo } from 'react'
import { Briefcase, Check, Inbox } from 'lucide-react'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { ProjectComboBoxProps } from '../type'
import { IssueCommandInput } from '@/components/app/combo-box/IssueCommandInput'
import { IconComboBox } from '@/components/app/combo-box/IconComboBox'
import { useComboBox } from '@/hooks/useComboBox'
import { cn } from '@/lib/utils'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { ResponsivePopover } from '@/components/ui/responsive-popover'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

const INBOX_VALUE = '__inbox__'

const getProjectIcon = (
  project: ProjectDocument,
  withHover: boolean = false,
) => {
  const baseClasses = 'h-4 w-4 shrink-0 transition-all duration-200'
  const hoverClasses = withHover ? 'hover:scale-125 hover:brightness-110' : ''

  return (
    <ProjectIcon
      iconType={project.iconType}
      icon={project.icon}
      iconColor={project.iconColor}
      className={cn(baseClasses, hoverClasses)}
      size={16}
    />
  )
}

interface ProjectGroup {
  parent: ProjectDocument
  children: Array<ProjectDocument>
}

function buildProjectTree(projects: Array<ProjectDocument>) {
  const rootProjects = projects.filter((p) => !p.parentProjectId)
  const childMap = new Map<string, Array<ProjectDocument>>()

  for (const project of projects) {
    if (project.parentProjectId) {
      const children = childMap.get(project.parentProjectId) ?? []
      children.push(project)
      childMap.set(project.parentProjectId, children)
    }
  }

  const groups: Array<ProjectGroup> = rootProjects.map((parent) => ({
    parent,
    children: childMap.get(parent.id) ?? [],
  }))

  // Orphan children (parent not in list)
  const orphans = projects.filter(
    (p) =>
      p.parentProjectId &&
      !rootProjects.some((r) => r.id === p.parentProjectId),
  )

  return { groups, orphans }
}

export function ProjectComboBox({
  projects,
  selectedProjectId,
  onProjectChange,
  placeholderText = 'Select project...',
  searchText = 'Search projects...',
  buttonClassName,
  isDisabled = false,
  variant = 'default',
  fontSize = 'xs',
  popoverOpen,
  onPopoverOpenChange,
  triggerProps,
  searchShortcutHint,
  showInbox = true,
  triggerContent,
}: ProjectComboBoxProps) {
  const handleValueChange = (value: string | null) => {
    if (value === INBOX_VALUE) {
      onProjectChange(null)
    } else {
      onProjectChange(value)
    }
  }

  const { isOpen, setIsOpen } = useComboBox({
    selectedValue: selectedProjectId,
    onValueChange: handleValueChange,
    popoverOpen,
    onPopoverOpenChange,
  })

  const textSizeClass =
    fontSize === 'xs' ? 'text-xs' : fontSize === 'sm' ? 'text-sm' : 'text-base'

  const { groups, orphans } = useMemo(
    () => buildProjectTree(projects),
    [projects],
  )

  const currentValue = selectedProjectId ?? (showInbox ? INBOX_VALUE : '')

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId),
    [projects, selectedProjectId],
  )

  // Icon-only variant uses the flat IconComboBox (only child projects)
  if (variant === 'icon-only') {
    const childProjects = projects.filter((p) => p.parentProjectId)
    const projectIconOptions = childProjects.map((project) => ({
      value: project.id,
      label: project.name,
      icon: getProjectIcon(project, true),
      keywords: [
        project.name.toLowerCase(),
        project.projectKey.toLowerCase(),
      ],
    }))

    if (showInbox) {
      projectIconOptions.unshift({
        value: INBOX_VALUE,
        label: 'Inbox',
        icon: <Inbox className="h-4 w-4" />,
        keywords: ['inbox', 'no project', 'unassigned'],
      })
    }

    return (
      <IconComboBox
        options={projectIconOptions}
        selectedValue={currentValue}
        onChange={handleValueChange}
        placeholderText={placeholderText}
        searchText={searchText}
        isDisabled={isDisabled}
        buttonClassName={buttonClassName}
        fontSize={fontSize}
        renderTriggerIcon={(option) =>
          option?.icon ?? <Briefcase className="h-4 w-4 text-muted-foreground" />
        }
        buttonAriaLabel={placeholderText}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        triggerProps={{ 'data-testid': 'project-select', ...triggerProps }}
        searchShortcutHint={searchShortcutHint}
      />
    )
  }

  const handleSelect = (value: string) => {
    handleValueChange(value)
    setIsOpen(false)
  }

  const triggerButton = (
    <Button
      data-testid="project-select"
      variant="outline"
      className={cn('justify-start text-xs h-8', buttonClassName)}
      disabled={isDisabled}
      {...triggerProps}
    >
      {triggerContent ?? <span className="text-foreground">
        {selectedProjectId === null && showInbox ? (
          <span className="flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            <span className={textSizeClass}>Inbox</span>
          </span>
        ) : selectedProject ? (
          <span className="flex items-center gap-2">
            {getProjectIcon(selectedProject)}
            <span className={textSizeClass}>{selectedProject.name}</span>
          </span>
        ) : (
          placeholderText
        )}
      </span>}
    </Button>
  )

  const commandContent = (
    <Command>
      <IssueCommandInput
        placeholder={searchText}
        shortcutHint={searchShortcutHint}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup>
          {/* Inbox */}
          {showInbox && (
            <CommandItem
              value={INBOX_VALUE}
              keywords={['inbox', 'no project', 'unassigned']}
              onSelect={() => handleSelect(INBOX_VALUE)}
              className="flex items-center gap-2"
            >
              <span className="flex flex-1 items-center gap-2">
                <Inbox className="h-4 w-4 shrink-0" />
                <span className={textSizeClass}>Inbox</span>
              </span>
              {currentValue === INBOX_VALUE && (
                <Check className="h-3.5 w-3.5 text-primary shrink-0" />
              )}
            </CommandItem>
          )}

          {/* Tree: space labels + selectable child projects */}
          {groups.map((group) => (
            <div key={group.parent.id}>
              {/* Space label (not selectable) */}
              <div className="flex items-center gap-2 px-2 py-1.5 mt-1">
                {getProjectIcon(group.parent)}
                <span className={cn(textSizeClass, 'font-medium text-muted-foreground')}>
                  {group.parent.name}
                </span>
              </div>

              {/* Child projects — indented with tree connector */}
              {group.children.map((child, idx) => {
                const isLast = idx === group.children.length - 1
                return (
                  <CommandItem
                    key={child.id}
                    value={child.id}
                    keywords={[
                      child.name.toLowerCase(),
                      child.projectKey.toLowerCase(),
                      group.parent.name.toLowerCase(),
                    ]}
                    onSelect={handleSelect}
                    className="flex items-center gap-2 pl-4"
                  >
                    <span className="text-muted-foreground/40 shrink-0 w-3 text-center select-none">
                      {isLast ? '└' : '├'}
                    </span>
                    <span className="flex flex-1 items-center gap-2">
                      {getProjectIcon(child, true)}
                      <span className={textSizeClass}>{child.name}</span>
                    </span>
                    {currentValue === child.id && (
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                    )}
                  </CommandItem>
                )
              })}
            </div>
          ))}

          {/* Orphan projects */}
          {orphans.map((project) => (
            <CommandItem
              key={project.id}
              value={project.id}
              keywords={[
                project.name.toLowerCase(),
                project.projectKey.toLowerCase(),
              ]}
              onSelect={handleSelect}
              className="flex items-center gap-2"
            >
              <span className="flex flex-1 items-center gap-2">
                {getProjectIcon(project, true)}
                <span className={textSizeClass}>{project.name}</span>
              </span>
              {currentValue === project.id && (
                <Check className="h-3.5 w-3.5 text-primary shrink-0" />
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )

  return (
    <ResponsivePopover
      open={isOpen}
      onOpenChange={setIsOpen}
      trigger={triggerButton}
      contentClassName="p-0 text-xs border-0"
      align="start"
    >
      {commandContent}
    </ResponsivePopover>
  )
}
